import '../Styles/ResultPage.css';
import MessageCard from '../MessageCard';
import { useLocation } from 'react-router-dom';
import { TwitterTweetEmbed } from 'react-twitter-embed';
import { getTwitterIdFromUrl } from '../../utils/ge-tweet-id-from-url';
import { Post } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto/post';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useState } from 'react';
import { useUrlParams } from '../../Hooks/useUrlParams';
import {
    createAssociatedTokenAccountIdempotentInstruction,
    createCloseAccountInstruction,
    createTransferInstruction,
    getAssociatedTokenAddressSync,
    NATIVE_MINT,
    TOKEN_PROGRAM_ID,
    unpackAccount,
} from '@solana/spl-token';
import { ComputeBudgetProgram, Connection, Keypair, LAMPORTS_PER_SOL, PublicKey, TransactionMessage, VersionedTransaction } from '@solana/web3.js';
import * as pumpfun from '../../utils/pumpfun/pump-fun';
import { AnchorProvider, BN, Program } from '@coral-xyz/anchor';
import MyWallet from '../../utils/pumpfun/my-wallet';
import pumpProgramIdl from '../../utils/pumpfun/pump-program';
import { getPumpSellInstructions } from '../../utils/Pumpfun';
import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes';
import { SELL_PRIORITY_FEE } from '../../utils/constants';
import { getZeroslotTipInstruction, sendZeroslotTransaction } from '../../utils/0slot';
import { useClipboard } from '../../Hooks/useClipboars';
import {
    getPdaCreatorVault,
    getPdaLaunchpadAuth,
    getPdaLaunchpadConfigId,
    getPdaLaunchpadPoolId,
    getPdaLaunchpadVaultId,
    getPdaPlatformVault,
    LaunchConstantProductCurve,
    LAUNCHPAD_PROGRAM,
    LaunchpadPool,
    sellExactInInstruction,
} from '@raydium-io/raydium-sdk-v2';
import { getProviderLink } from '../../utils/bubbles/link';

interface Props {
    solPrivateKey: string;
    solSniperPrivateKey: string;
    zeroSlotSellTip: number;
    solConnection: Connection;
}

export function Result({ solPrivateKey, solSniperPrivateKey, zeroSlotSellTip, solConnection }: Props) {
    const location = useLocation();
    const { walletAddress } = useUrlParams();
    const {
        post,
        background,
        platform,
        mint,
        name,
        symbol,
        initBuyDeployer,
        initBuySniper,
        takeProfitPercent,
        autoSellDelaySeconds,
        imageFile,
        dex,
    }: {
        post: Post;
        background: string;
        platform: string;
        mint: string;
        name: string;
        symbol: string;
        initBuyDeployer: number;
        initBuySniper: number;
        takeProfitPercent: number;
        autoSellDelaySeconds: number;
        imageFile: File;
        dex: 'pump' | 'bonk';
    } = location.state || {};
    const [imageSrc, setImageSrc] = useState<string | null>(null);

    const [solProfitDeployer, setSolProfitDeployer] = useState<number>(0);
    const [currentProfitPercentDeployer, setCurrentProfitPercentDeployer] = useState<number>(0);
    const [solProfitSniper, setSolProfitSniper] = useState<number>(0);
    const [currentProfitPercentSniper, setCurrentProfitPercentSniper] = useState<number>(0);

    const [autoSellTimeLeft, setAutoSellTimeLeft] = useState<number>(autoSellDelaySeconds);
    const [autoSellTimer, setAutoSellTimer] = useState<boolean>(true);
    const [autoSellProfit, setAutoSellProfit] = useState<boolean>(true);

    const [tokenBalanceDeployer, setTokenBalanceDeployer] = useState<string>('0');
    const [transferPercentDeployer, setTransferPercentDeployer] = useState<number>(100);
    const [transferWalletDeployer, setTransferWalletDeployer] = useState<string>('');

    const [tokenBalanceSniper, setTokenBalanceSniper] = useState<string>('0');
    const [transferPercentSniper, setTransferPercentSniper] = useState<number>(100);
    const [transferWalletSniper, setTransferWalletSniper] = useState<string>('');

    useEffect(() => {
        const walletsToTrack = [
            {
                privateKey: solPrivateKey,
                setBalance: setTokenBalanceDeployer,
            },
            {
                privateKey: solSniperPrivateKey,
                setBalance: setTokenBalanceSniper,
            },
        ];

        const subs: number[] = [];

        walletsToTrack.forEach(({ privateKey, setBalance }) => {
            try {
                if (!privateKey || !mint) return;

                const wallet = Keypair.fromSecretKey(Uint8Array.from(bs58.decode(privateKey)));
                const tokenAccount = getAssociatedTokenAddressSync(new PublicKey(mint), wallet.publicKey);

                solConnection
                    .getTokenAccountBalance(tokenAccount, 'processed')
                    .then((res) => {
                        setBalance(res.value.uiAmountString || '0');
                    })
                    .catch(() => {
                        setBalance('0');
                    });

                const subId = solConnection.onAccountChange(
                    tokenAccount,
                    async (accountInfo) => {
                        try {
                            const account = unpackAccount(tokenAccount, accountInfo);
                            const balance = (account.amount / BigInt(1e6)).toString();
                            setBalance(balance);
                        } catch {
                            setBalance('0');
                        }
                    },
                    { commitment: 'processed' },
                );

                subs.push(subId);
            } catch (err) {
                console.error('Error tracking token balance:', err);
            }
        });

        return () => {
            subs.forEach((subId) => {
                solConnection.removeAccountChangeListener(subId);
            });
        };
    }, [solPrivateKey, solSniperPrivateKey, mint, solConnection]);

    useEffect(() => {
        if (imageFile) {
            const imageUrl = URL.createObjectURL(imageFile);
            setImageSrc(imageUrl);
            return () => URL.revokeObjectURL(imageUrl);
        }
    }, [imageFile]);

    const sell = useCallback(
        async function sell(percent: number, privateKey: string) {
            try {
                if (!walletAddress) {
                    toast.error('Wallet address not found');
                    return;
                }
                const ata = getAssociatedTokenAddressSync(new PublicKey(mint), new PublicKey(walletAddress));

                const balanceResponce = (await solConnection.getTokenAccountBalance(ata)).value;
                if (balanceResponce.uiAmount === 0) {
                    toast.error('No balance');
                    return;
                }
                toast.info(`Selling ${(balanceResponce.uiAmount! * percent) / 100} ${symbol}...`);
                const sellAmount = Math.floor((Number(balanceResponce.amount) * percent) / 100);

                const seller = Keypair.fromSecretKey(Uint8Array.from(bs58.decode(privateKey)));
                const ix =
                    dex === 'pump'
                        ? await getPumpSellInstructions(seller, new PublicKey(mint), BigInt(sellAmount), BigInt(0))
                        : sellExactInInstruction(
                              LAUNCHPAD_PROGRAM,
                              seller.publicKey,
                              getPdaLaunchpadAuth(LAUNCHPAD_PROGRAM).publicKey,
                              getPdaLaunchpadConfigId(LAUNCHPAD_PROGRAM, NATIVE_MINT, 0, 0).publicKey,
                              new PublicKey('FfYek5vEz23cMkWsdJwG2oa6EphsvXSHrGpdALN4g6W1'), //letsbonk
                              getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, new PublicKey(mint), NATIVE_MINT).publicKey,
                              getAssociatedTokenAddressSync(new PublicKey(mint), seller.publicKey),
                              getAssociatedTokenAddressSync(NATIVE_MINT, seller.publicKey),
                              getPdaLaunchpadVaultId(
                                  LAUNCHPAD_PROGRAM,
                                  getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, new PublicKey(mint), NATIVE_MINT).publicKey,
                                  new PublicKey(mint),
                              ).publicKey,
                              getPdaLaunchpadVaultId(
                                  LAUNCHPAD_PROGRAM,
                                  getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, new PublicKey(mint), NATIVE_MINT).publicKey,
                                  NATIVE_MINT,
                              ).publicKey,
                              new PublicKey(mint),
                              NATIVE_MINT,
                              TOKEN_PROGRAM_ID,
                              TOKEN_PROGRAM_ID,
                              getPdaPlatformVault(LAUNCHPAD_PROGRAM, new PublicKey('FfYek5vEz23cMkWsdJwG2oa6EphsvXSHrGpdALN4g6W1'), NATIVE_MINT).publicKey,
                              getPdaCreatorVault(LAUNCHPAD_PROGRAM, seller.publicKey, NATIVE_MINT).publicKey,
                              sellAmount,
                              0,
                          );
                const { blockhash } = await solConnection.getLatestBlockhash('confirmed');
                console.log('zeroslot tip', zeroSlotSellTip);
                const tx = new VersionedTransaction(
                    new TransactionMessage({
                        payerKey: seller.publicKey,
                        recentBlockhash: blockhash,
                        instructions: [
                            createAssociatedTokenAccountIdempotentInstruction(
                                seller.publicKey,
                                getAssociatedTokenAddressSync(NATIVE_MINT, seller.publicKey),
                                seller.publicKey,
                                NATIVE_MINT,
                            ),
                            ix,
                            createCloseAccountInstruction(getAssociatedTokenAddressSync(NATIVE_MINT, seller.publicKey), seller.publicKey, seller.publicKey),
                            ComputeBudgetProgram.setComputeUnitPrice({ microLamports: SELL_PRIORITY_FEE }),
                            getZeroslotTipInstruction(seller.publicKey, zeroSlotSellTip * LAMPORTS_PER_SOL),
                        ],
                    }).compileToV0Message(),
                );
                tx.sign([seller]);
                const signature = await sendZeroslotTransaction(tx);
                toast.success(`Selling tx sent ${signature}`);
            } catch (error) {
                toast.error('Error selling');
                console.error('Error selling:', error);
            }
        },
        [mint, solPrivateKey, symbol, walletAddress, zeroSlotSellTip],
    );

    useEffect(() => {
        const subs: number[] = [];
        const wallets = [
            {
                name: 'deployer',
                privateKey: solPrivateKey,
                tokenBalance: tokenBalanceDeployer,
                setTokenBalance: setTokenBalanceDeployer,
                setSolProfit: setSolProfitDeployer,
                setProfitPercent: setCurrentProfitPercentDeployer,
                initBuy: initBuyDeployer,
            },
            {
                name: 'sniper',
                privateKey: solSniperPrivateKey,
                tokenBalance: tokenBalanceSniper,
                setTokenBalance: setTokenBalanceSniper,
                setSolProfit: setSolProfitSniper,
                setProfitPercent: setCurrentProfitPercentSniper,
                initBuy: initBuySniper,
            },
        ];
        wallets.forEach(({ privateKey, tokenBalance, setTokenBalance, setSolProfit, setProfitPercent, initBuy }) => {
            try {
                if (!privateKey || !mint) return;

                const wallet = Keypair.fromSecretKey(Uint8Array.from(bs58.decode(privateKey)));

                if (dex === 'pump') {
                    const provider = new AnchorProvider(solConnection, new MyWallet(Keypair.generate()), AnchorProvider.defaultOptions());
                    const program = new Program(pumpProgramIdl, pumpfun.PROGRAM_PUBKEY, provider);
                    const bondingCurvePubkey = pumpfun.getBondingCurvePubkey(new PublicKey(mint));

                    const subId = solConnection.onAccountChange(
                        bondingCurvePubkey,
                        async (accountInfo) => {
                            try {
                                const bondingCurveData = program.coder.accounts.decode('BondingCurve', accountInfo.data) as pumpfun.BondingCurveData;
                                const tokenAccount = getAssociatedTokenAddressSync(new PublicKey(mint), wallet.publicKey);
                                const balanceRes = await solConnection.getTokenAccountBalance(tokenAccount);

                                const balanceUi = balanceRes.value.uiAmountString || '0';
                                setTokenBalance(balanceUi);

                                const balance = balanceRes.value.amount;
                                const tokenWorth = pumpfun.getMinSolAmount(new BN(balance), bondingCurveData);
                                const solProfit = tokenWorth.toNumber() - initBuy * LAMPORTS_PER_SOL;
                                const currentProfitPercent = (solProfit / (initBuy * LAMPORTS_PER_SOL)) * 100;

                                setSolProfit(solProfit);
                                setProfitPercent(currentProfitPercent);
                            } catch (error) {
                                console.error('Error decoding bonding curve data:', error);
                            }
                        },
                        { commitment: 'processed' },
                    );

                    subs.push(subId);
                } else if (dex === 'bonk') {
                    const poolId = getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, new PublicKey(mint), NATIVE_MINT).publicKey;

                    const convertAccountDataToProfit = (accountData: Buffer) => {
                        try {
                            const poolData = LaunchpadPool.decode(accountData);
                            const amount = new BN(Math.floor(Number(tokenBalance) * 1e6 * 0.99));
                            const minSolAmount = LaunchConstantProductCurve.sellExactIn({ poolInfo: poolData, amount });

                            const solProfit = minSolAmount.sub(new BN(initBuy * LAMPORTS_PER_SOL)).toNumber();
                            const currentProfitPercent = (solProfit / (initBuy * LAMPORTS_PER_SOL)) * 100;

                            setSolProfit(solProfit);
                            setProfitPercent(currentProfitPercent);
                        } catch (e) {
                            console.error('Error decoding BONK curve:', e);
                        }
                    };

                    solConnection.getAccountInfo(poolId, 'processed').then((acc) => {
                        if (acc) convertAccountDataToProfit(acc.data);
                    });

                    const subId = solConnection.onAccountChange(
                        poolId,
                        async (accData) => {
                            convertAccountDataToProfit(accData.data);
                        },
                        { commitment: 'processed' },
                    );

                    subs.push(subId);
                }
            } catch (e) {
                console.error('Wallet profit tracking error:', e);
            }
        });

        return () => {
            subs.forEach((subId) => {
                solConnection.removeAccountChangeListener(subId);
            });
        };
    }, [solPrivateKey, solSniperPrivateKey, mint, dex, tokenBalanceDeployer, tokenBalanceSniper, initBuyDeployer, initBuySniper, solConnection]);

    useEffect(() => {
        const wallets = [
            {
                privateKey: solPrivateKey,
                currentProfitPercent: currentProfitPercentDeployer,
                setAutoSellProfit: setAutoSellProfit,
                autoSellProfit: autoSellProfit,
            },
            {
                privateKey: solSniperPrivateKey,
                currentProfitPercent: currentProfitPercentSniper,
                setAutoSellProfit: setAutoSellProfit,
                autoSellProfit: autoSellProfit,
            },
        ];

        wallets.forEach(({ autoSellProfit, currentProfitPercent, privateKey, setAutoSellProfit }) => {
            if (!autoSellProfit) return;
            if (currentProfitPercent < takeProfitPercent) return;

            sell(100, privateKey);
            setAutoSellProfit(false);
        });
    }, [autoSellProfit, currentProfitPercentDeployer, currentProfitPercentSniper, takeProfitPercent, sell, solPrivateKey, solSniperPrivateKey]);

    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (autoSellTimer) {
            interval = setInterval(() => {
                setAutoSellTimeLeft((prev) => {
                    if (prev > 1) {
                        return prev - 1;
                    } else {
                        clearInterval(interval!);
                        setAutoSellTimer(false);
                        sell(100, solPrivateKey);
                        sell(100, solSniperPrivateKey);
                        return 0;
                    }
                });
            }, 1000);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [autoSellTimer, sell]);

    const { copyToClipboard } = useClipboard();

    async function transfer(sender: string, receiver: string, percent: number) {
        try {
            toast.info(`Transferring ${percent}% to ${receiver}...`);
            const senderAta = getAssociatedTokenAddressSync(new PublicKey(mint), new PublicKey(sender));
            const balance = await solConnection.getTokenAccountBalance(senderAta, 'processed');
            if (!balance.value) {
                return toast.error('No balance');
            }
            const amount = Math.floor((Number(balance.value.amount) * percent) / 100);

            const tx = new VersionedTransaction(
                new TransactionMessage({
                    payerKey: new PublicKey(sender),
                    recentBlockhash: (await solConnection.getLatestBlockhash('confirmed')).blockhash,
                    instructions: [
                        createAssociatedTokenAccountIdempotentInstruction(
                            new PublicKey(sender),
                            getAssociatedTokenAddressSync(new PublicKey(mint), new PublicKey(receiver)),
                            new PublicKey(receiver),
                            new PublicKey(mint),
                        ),
                        createTransferInstruction(
                            getAssociatedTokenAddressSync(new PublicKey(mint), new PublicKey(sender)),
                            getAssociatedTokenAddressSync(new PublicKey(mint), new PublicKey(receiver)),
                            new PublicKey(sender),
                            amount,
                        ),
                        getZeroslotTipInstruction(new PublicKey(sender), zeroSlotSellTip * LAMPORTS_PER_SOL),
                    ],
                }).compileToV0Message(),
            );
            tx.sign([Keypair.fromSecretKey(Uint8Array.from(bs58.decode(solPrivateKey)))]);

            const sign = await sendZeroslotTransaction(tx);

            toast.success(`Transfer tx sent ${sign}`);
        } catch (error) {
            toast.error('Error transferring');
            console.error('Error transferring:', error);
        }
    }

    return (
        <section className='card-info-container'>
            <section className='card'>
                <div className='mes-card-form'>
                    {post ? <MessageCard post={post} background={background} platform={platform} username={post.username} /> : <p>Post not selected</p>}
                </div>
                <div className='tweetForm'>
                    {post && (post.url.includes('twitter.com') || post.url.includes('x.com')) ? (
                        <div data-theme='dark'>
                            <div
                                data-theme='dark'
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    overflow: 'hidden',
                                }}
                            >
                                <TwitterTweetEmbed tweetId={getTwitterIdFromUrl(post.url)} options={{ theme: 'dark' }} />
                            </div>
                        </div>
                    ) : (
                        <p></p>
                    )}
                </div>
            </section>
            <section className='pump-info'>
                <div className='icon-link'>
                    <div
                        className={`icon ${dex === 'bonk' ? 'bonk-icon' : 'pump-icon'}`}
                        onClick={() => {
                            window.open(getProviderLink(dex, mint), '_blank');
                        }}
                    ></div>
                    <div className='pump-token-adress'>
                        <h3>Instant AI Token Created!</h3>
                        <p
                            onClick={() => {
                                window.open(getProviderLink(dex, mint), '_blank');
                            }}
                        >
                            {mint}
                        </p>
                        <button className='copy-clipboard' onClick={() => copyToClipboard(mint)}></button>
                    </div>
                </div>
                <div className='name-symbol-image'>
                    <div className='name-symbol'>
                        <div className='name'>
                            <h3>Name: {name}</h3>
                        </div>
                        <div className='symbol'>
                            <h3>Symbol: {symbol}</h3>
                        </div>
                        <div className='initial-buy'>
                            <h3>Initial Buy: {initBuyDeployer.toFixed(3)}</h3>
                        </div>
                    </div>
                    <div className='token-image'>
                        <h3>Image</h3>
                        <div className='token-img'>
                            {imageSrc && (
                                <img
                                    src={imageSrc}
                                    alt='Token'
                                    className='image-preview'
                                    onClick={() => {
                                        window.open(getProviderLink(dex, mint), '_blank');
                                    }}
                                />
                            )}
                        </div>
                        <div
                            className={`icon ${dex === 'bonk' ? 'bonk-icon' : 'pump-icon'}`}
                            onClick={() => {
                                window.open(getProviderLink(dex, mint), '_blank');
                            }}
                        ></div>
                    </div>
                </div>
                <hr />
                {autoSellTimer ? (
                    <div className='autosell-in'>
                        <div className='autosell-text'>
                            <h3>Auto Sell In</h3>
                            <p>{autoSellTimeLeft} Sec</p>
                        </div>
                        <button className='sell-btn' onClick={() => setAutoSellTimer(false)}>
                            Cancel
                        </button>
                    </div>
                ) : null}
                <div className='profit'>
                    <div className='profit-text'>
                        <h3>Profit</h3>
                        <div className='profit-percent-text'>
                            <h2 className='percent-sol'>{`${(solProfitDeployer / LAMPORTS_PER_SOL).toFixed(4)} (${currentProfitPercentDeployer.toFixed(
                                2,
                            )}%)`}</h2>
                            {autoSellProfit ? <p>Auto Sell At {takeProfitPercent}% Profit Goal</p> : null}
                        </div>
                    </div>
                    {autoSellProfit ? (
                        <button className='sell-btn' onClick={() => setAutoSellProfit(false)}>
                            Cancel
                        </button>
                    ) : null}
                </div>

                <div className='button-container'>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(100, solPrivateKey);
                        }}
                    >
                        Sell 100%
                    </button>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(50, solPrivateKey);
                        }}
                    >
                        Sell 50%
                    </button>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(25, solPrivateKey);
                        }}
                    >
                        Sell 25%
                    </button>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(10, solPrivateKey);
                        }}
                    >
                        Sell 10%
                    </button>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(5, solPrivateKey);
                        }}
                    >
                        Sell 5%
                    </button>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(1, solPrivateKey);
                        }}
                    >
                        Sell 1%
                    </button>
                </div>
                <div className='tkn-balance'>
                    <h3>Token Balance:</h3>
                    <p>{tokenBalanceDeployer}</p>
                </div>
                <div className='transfer-tkns'>
                    <h3>Transfer Tokens To Wallet</h3>
                    <div className='inp-pecrent'>
                        <input
                            type='number'
                            className='pecrent-inp'
                            value={transferPercentDeployer}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                if (value >= 0 && value <= 100) {
                                    setTransferPercentDeployer(value);
                                } else if (value < 0) {
                                    setTransferPercentDeployer(0);
                                } else if (value > 100) {
                                    setTransferPercentDeployer(100);
                                }
                            }}
                        />
                        <h3>%</h3>
                    </div>
                    <input
                        type='string'
                        className='wallet-inp'
                        value={transferWalletDeployer}
                        onChange={(e) => {
                            setTransferWalletDeployer(e.target.value);
                        }}
                    />
                    <button
                        className='sell-btn'
                        onClick={() => {
                            transfer(walletAddress, transferWalletDeployer, transferPercentDeployer);
                        }}
                    >
                        Transfer
                    </button>
                </div>

                <hr />
                <div className='profit'>
                    <div className='profit-text'>
                        <h3>Profit</h3>
                        <div className='profit-percent-text'>
                            <h2 className='percent-sol'>{`${(solProfitSniper / LAMPORTS_PER_SOL).toFixed(4)} (${currentProfitPercentSniper.toFixed(2)}%)`}</h2>
                            <p>Auto Sell At {takeProfitPercent}% Profit Goal</p>
                        </div>
                    </div>
                    {autoSellProfit ? (
                        <button className='sell-btn' onClick={() => setAutoSellProfit(false)}>
                            Cancel
                        </button>
                    ) : null}
                </div>

                <div className='button-container'>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(100, solSniperPrivateKey);
                        }}
                    >
                        Sell 100%
                    </button>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(50, solSniperPrivateKey);
                        }}
                    >
                        Sell 50%
                    </button>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(25, solSniperPrivateKey);
                        }}
                    >
                        Sell 25%
                    </button>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(10, solSniperPrivateKey);
                        }}
                    >
                        Sell 10%
                    </button>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(5, solSniperPrivateKey);
                        }}
                    >
                        Sell 5%
                    </button>
                    <button
                        className='sell-btn'
                        onClick={() => {
                            sell(1, solSniperPrivateKey);
                        }}
                    >
                        Sell 1%
                    </button>
                </div>
                <div className='tkn-balance'>
                    <h3>Token Balance:</h3>
                    <p>{tokenBalanceSniper}</p>
                </div>
                <div className='transfer-tkns'>
                    <h3>Transfer Tokens To Wallet</h3>
                    <div className='inp-pecrent'>
                        <input
                            type='number'
                            className='pecrent-inp'
                            value={transferPercentSniper}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                if (value >= 0 && value <= 100) {
                                    setTransferPercentSniper(value);
                                } else if (value < 0) {
                                    setTransferPercentSniper(0);
                                } else if (value > 100) {
                                    setTransferPercentSniper(100);
                                }
                            }}
                        />
                        <h3>%</h3>
                    </div>
                    <input
                        type='string'
                        className='wallet-inp'
                        value={transferWalletSniper}
                        onChange={(e) => {
                            setTransferWalletSniper(e.target.value);
                        }}
                    />
                    <button
                        className='sell-btn'
                        onClick={() => {
                            transfer(solSniperPrivateKey, transferWalletSniper, transferPercentSniper);
                        }}
                    >
                        Transfer
                    </button>
                </div>
            </section>
        </section>
    );
}
